import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HomeIcon from '@mui/icons-material/Home';
import { cancelDemoTour } from './DemoTour';
import { useDemo } from '../../contexts/DemoContext';
import { DemoPersonaRole } from '../../services/demoService';
import { getDemoRouteForRole } from '../../utils/roleNavigation';
import { APP_CHROME_OFFSET_VAR } from '../../theme/chromeOffset';

/**
 * The always-visible bar that lets a visitor move between the four views without logging in.
 *
 * Rendered above the app's own chrome, deliberately distinct from it, so nobody mistakes the demo
 * controls for part of the product.
 */

/** Height of the Toolbar below. The one place this number lives — everything that needs to know
 * how tall the bar is (the offset it publishes, its own layout) reads it from here. */
export const PERSONA_BAR_HEIGHT = 52;

interface PersonaTab {
  role: DemoPersonaRole;
  label: string;
  hint: string;
  icon: React.ReactElement;
}

const PERSONA_TABS: PersonaTab[] = [
  { role: 'student', label: 'Student', hint: 'Solve problems, earn badges, climb the leaderboard', icon: <SchoolIcon /> },
  { role: 'teacher', label: 'Coach', hint: 'Track a roster and spot students who are struggling', icon: <GroupsIcon /> },
  { role: 'parent', label: 'Parent', hint: 'Follow your children and enrol them in a session', icon: <FamilyRestroomIcon /> },
  { role: 'admin', label: 'Admin', hint: 'Build sessions, run reports, brand the site', icon: <SettingsIcon /> },
];

const formatRemaining = (expiresAt: Date): string => {
  const remainingMs = expiresAt.getTime() - Date.now();

  if (remainingMs <= 0) {
    return 'expired';
  }

  const minutes = Math.floor(remainingMs / 60000);

  return minutes >= 60
    ? `${Math.floor(minutes / 60)}h ${minutes % 60}m left`
    : `${minutes}m left`;
};

export default function PersonaBar() {
  const { enabled, session, activeRole, loading, error, switchPersona, restartDemo, expiresAt } =
    useDemo();
  const navigate = useNavigate();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const [remaining, setRemaining] = useState<string>('');

  useEffect(() => {
    if (!expiresAt) {
      return;
    }

    const tick = () => setRemaining(formatRemaining(expiresAt));
    tick();
    const timer = window.setInterval(tick, 30000);

    return () => window.clearInterval(timer);
  }, [expiresAt]);

  // Publish how tall this bar is so fixed/sticky app chrome (sidebars, app bars) can offset below
  // it instead of resolving `top: 0` against the viewport and rendering underneath it. Cleared on
  // unmount so a build without the demo — or a demo visitor who somehow loses their session — is
  // indistinguishable from one that never had this property set.
  useEffect(() => {
    if (!enabled || !session) {
      return;
    }

    document.documentElement.style.setProperty(APP_CHROME_OFFSET_VAR, `${PERSONA_BAR_HEIGHT}px`);

    return () => {
      document.documentElement.style.removeProperty(APP_CHROME_OFFSET_VAR);
    };
  }, [enabled, session]);

  if (!enabled || !session) {
    return null;
  }

  const availableRoles = new Set(session.personas.map(persona => persona.role));
  const tabs = PERSONA_TABS.filter(tab => availableRoles.has(tab.role));
  const activeIndex = tabs.findIndex(tab => tab.role === activeRole);

  const handleSwitch = async (role: DemoPersonaRole) => {
    // switchPersona never rejects — a failure sets `error` (surfaced below via the Snackbar) and
    // resolves false. Navigating anyway would send an unauthenticated visitor into a route guarded
    // for a persona they never actually became, which redirects to /login — a login form, on a
    // product whose whole pitch is "no sign-up, no password".
    const switched = await switchPersona(role);

    if (switched) {
      navigate(getDemoRouteForRole(role));
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          // Above the drawer, not just above the app bar. The student layout uses a permanent MUI
          // Drawer, whose default z-index (1200) is higher than appBar (1100) — at appBar + 2 the
          // sidebar covered the left of this bar and hid the Student tab entirely.
          zIndex: theme.zIndex.drawer + 2,
          backgroundColor: theme.palette.grey[900],
          color: theme.palette.common.white,
        }}
      >
        <Toolbar variant="dense" sx={{ gap: 2, minHeight: PERSONA_BAR_HEIGHT }}>
          {/* The only way back to the landing page. Without it a visitor who has entered the
              sandbox is stuck inside the app: every other control here moves them between
              personas, and the app's own chrome has no concept of a demo homepage to return to.
              Cancels any pending tour so returning here is never undone by a tour step
              navigating away again. */}
          <Tooltip title="Back to the demo overview" arrow>
            <Button
              data-demo-id="demo-home"
              size="small"
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => {
                cancelDemoTour();
                navigate('/');
              }}
              sx={{ textTransform: 'none', fontWeight: 700, letterSpacing: 0.5, flexShrink: 0 }}
            >
              {compact ? '' : 'Interactive demo'}
            </Button>
          </Tooltip>

          <Tabs
            value={activeIndex === -1 ? false : activeIndex}
            onChange={(_event, index: number) => handleSwitch(tabs[index].role)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="inherit"
            TabIndicatorProps={{ sx: { height: 3, backgroundColor: theme.palette.primary.light } }}
            sx={{ flexGrow: 1, minHeight: PERSONA_BAR_HEIGHT }}
          >
            {tabs.map(tab => (
              <Tooltip key={tab.role} title={tab.hint} arrow>
                <Tab
                  data-demo-id={`persona-${tab.role}`}
                  icon={tab.icon}
                  iconPosition="start"
                  label={compact ? undefined : tab.label}
                  aria-label={`View the demo as a ${tab.label.toLowerCase()}`}
                  sx={{ minHeight: PERSONA_BAR_HEIGHT, textTransform: 'none', fontWeight: 600 }}
                />
              </Tooltip>
            ))}
          </Tabs>

          {loading && <CircularProgress size={18} sx={{ color: 'inherit' }} />}

          {expiresAt && !compact && (
            <Chip
              size="small"
              label={remaining}
              variant="outlined"
              sx={{ color: 'inherit', borderColor: 'rgba(255,255,255,0.4)' }}
            />
          )}

          <Tooltip title="Discard everything you have changed and start over" arrow>
            <Box component="span">
              <Button
                data-demo-id="demo-restart"
                size="small"
                color="inherit"
                startIcon={<RestartAltIcon />}
                onClick={restartDemo}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                {compact ? '' : 'Restart'}
              </Button>
            </Box>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Snackbar open={Boolean(error)} autoHideDuration={6000}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
